import { AppIcon, type College, type ColorToken } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { CollegeTier } from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, ButtonBase, IconButton, SxProps, Theme, Tooltip, Typography } from '@mui/material';

import { myCollegeTooltip, useMyCollegeToggle } from './useMyCollegeToggle';

const STRINGS = appStrings.modules.myColleges;

type BadgeColors = Readonly<{
  background: ColorToken;
  border: ColorToken;
  dot: ColorToken;
  dotBorder: ColorToken;
  text: ColorToken;
}>;

const BADGE_COLORS: Record<CollegeTier | 'none', BadgeColors> = {
  safety: {
    background: 'Warning/100',
    border: 'Warning/200',
    dot: 'Orange/50',
    dotBorder: 'Orange/200',
    text: 'Warning/700',
  },
  target: {
    background: 'Success/100',
    border: 'Success/200',
    dot: 'Colors/Base/White',
    dotBorder: 'Success/200',
    text: 'Success/700',
  },
  reach: {
    background: 'Brand/100',
    border: 'Brand/200',
    dot: 'Colors/Base/White',
    dotBorder: 'Brand/200',
    text: 'Brand/700',
  },
  none: {
    background: 'Gray/100',
    border: 'Gray/200',
    dot: 'Colors/Base/White',
    dotBorder: 'Gray/200',
    text: 'Gray/700',
  },
};

export const MyCollegeTitleControl = ({ college }: Readonly<{ college: College }>) => {
  const myCollege = useMyCollegeToggle(college);
  const { isAvailable, isInMyColleges, tier, toggle } = myCollege;

  if (!isAvailable) return null;

  if (!isInMyColleges) {
    return (
      <Tooltip title={myCollegeTooltip(myCollege) || STRINGS.add}>
        <IconButton
          id="college-my-colleges-add"
          aria-label={STRINGS.add}
          onClick={toggle}
          sx={addButtonSx}
        >
          <AppIcon iconName="Plus" diameter="16px" color="Brand/600" />
        </IconButton>
      </Tooltip>
    );
  }

  const colors = BADGE_COLORS[tier ?? 'none'];

  return (
    <Tooltip title={myCollegeTooltip(myCollege)}>
      <ButtonBase
        id="college-my-colleges-tier"
        aria-label={STRINGS.removeAriaLabel(college.school_name)}
        onClick={toggle}
        sx={badgeSx(colors)}
      >
        <Box component="span" sx={dotSx(colors)}>
          <AppIcon iconName="Check" diameter="12px" color={colors.text} />
        </Box>
        <Typography component="span" variant="text-sm-medium">
          {tier ? STRINGS.tierLabels[tier] : STRINGS.inList}
        </Typography>
      </ButtonBase>
    </Tooltip>
  );
};

const addButtonSx: SxProps<Theme> = ({ backrs }) => ({
  width: backrs.spacing['spacing-4xl'],
  height: backrs.spacing['spacing-4xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Brand/25'],
  '&:hover': { backgroundColor: backrs.colors['Brand/50'] },
});

const badgeSx =
  (colors: BadgeColors): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-lg'],
    paddingBlock: backrs.spacing['spacing-xs'],
    paddingInlineStart: backrs.spacing['spacing-xs'],
    paddingInlineEnd: backrs.spacing['spacing-lg'],
    borderRadius: backrs.radius['radius-full'],
    border: `1px solid ${backrs.colors[colors.border]}`,
    backgroundColor: backrs.colors[colors.background],
    color: backrs.colors[colors.text],
  });

const dotSx =
  (colors: BadgeColors): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'inline-flex',
    padding: backrs.spacing['spacing-sm'],
    borderRadius: backrs.radius['radius-full'],
    border: `1px solid ${backrs.colors[colors.dotBorder]}`,
    backgroundColor: backrs.colors[colors.dot],
  });
